import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Handshake as ReferralIcon,
} from '@mui/icons-material';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';

import { ClinicDialog } from '../components/dialogs/ClinicDialog';
import ConfirmDeleteDialog from '../components/dialogs/ConfirmDeleteDialog';
import { CLINIC_DIALOG_SX } from '../constants/popupStyles';
import { useAuth } from '../context/AuthContext';
import { referralService, Referral, ReferralCreate } from '../services/referralService';
import RequirePermission from '../components/common/RequirePermission';
import { useCrudHandlers } from '../hooks/useCrudHandlers';
import ManageColumnsModal from '../components/modals/ManageColumnsModal';
import SaveViewModal from '../components/modals/SaveViewModal';
import DataTableToolbar from '../components/table/DataTableToolbar';
import DataTableContainer from '../components/table/DataTableContainer';
import DataTableBody from '../components/table/DataTableBody';
import MobileCardGrid from '../components/table/MobileCardGrid';
import TablePageLayout from '../components/table/TablePageLayout';
import useTablePage from '../components/table/hooks/useTablePage';
import { referralColumns } from '../config/referralColumns';
import { formatDate } from '../utils/dateUtils';
import { RowContextMenu, ContextMenuOption } from '../components/ui/RowContextMenu';

const ReferralsPage: React.FC = () => {
  const { user: currentUser } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const canManageReferrals = !!currentUser?.user_privilege?.referral;

  // UI state
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editingReferral, setEditingReferral] = useState<Referral | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [logoLoading, setLogoLoading] = useState(false);
  const [pendingLogoKey, setPendingLogoKey] = useState<string | null>(null);
  const [pendingLogoPreview, setPendingLogoPreview] = useState<string | null>(null);
  const logoInputRef = useRef<HTMLInputElement>(null);

  const { register, handleSubmit, reset, setValue, formState: { errors } } = useForm<ReferralCreate>();

  const getSortValue = useCallback((referral: Referral, key: string): any => {
    switch (key) {
      case 'name': return referral.name.toLowerCase();
      case 'phone': return referral.phone || '';
      case 'email': return (referral.email || '').toLowerCase();
      case 'createdAt': return referral.created_at;
      default: return '';
    }
  }, []);

  const getSearchText = useCallback((referral: Referral): string[] => {
    return [referral.name, referral.phone || '', referral.email || ''];
  }, []);

  const table = useTablePage<Referral>({
    entityType: 'referrals',
    columns: referralColumns,
    tenantId: currentUser?.tenant_id,
    getSortValue,
    getSearchText,
  });

  const clearPendingLogo = () => {
    if (pendingLogoPreview) URL.revokeObjectURL(pendingLogoPreview);
    setPendingLogoKey(null);
    setPendingLogoPreview(null);
  };

  const crud = useCrudHandlers<Referral>({
    table,
    createFn: (data) => referralService.createReferral(data),
    updateFn: (id, data) => referralService.updateReferral(id as number, data),
    deleteFn: (id) => referralService.deleteReferral(id as number),
    entityName: 'Referral',
    onSuccess: () => { setAddDialogOpen(false); setEditingReferral(null); reset(); clearPendingLogo(); },
    onDeleteSuccess: () => { setDeleteDialogOpen(false); },
  });

  // Load data
  useEffect(() => {
    if (canManageReferrals) {
      table.loadData(() => referralService.getReferrals());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  const openEditDialog = (referral: Referral) => {
    setEditingReferral(referral);
    reset({
      name: referral.name,
      phone: referral.phone || '',
      email: referral.email || '',
      logo: referral.logo || '',
    });
    clearPendingLogo();
    setAddDialogOpen(true);
  };

  const handleEdit = () => {
    if (table.selectedItem) openEditDialog(table.selectedItem);
    table.handleMenuClose();
  };

  const handleDelete = () => {
    if (table.selectedItem) setDeleteDialogOpen(true);
    table.handleMenuClose();
  };

  const handleAddReferral = () => {
    setEditingReferral(null);
    reset({
      name: '',
      phone: '',
      email: '',
      logo: '',
    });
    clearPendingLogo();
    setAddDialogOpen(true);
  };

  const handleLogoFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !editingReferral) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Please upload a JPEG, PNG, or WebP image.');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error('File is too large. Maximum size is 2MB.');
      return;
    }

    setLogoLoading(true);
    const previewUrl = URL.createObjectURL(file);
    try {
      const { s3_key } = await referralService.uploadLogo(editingReferral.id, file);
      setPendingLogoKey(s3_key);
      setValue('logo', s3_key);
      setPendingLogoPreview(previewUrl);
      setEditingReferral({ ...editingReferral, logo: s3_key });
      toast.success('Logo ready — click Update to save');
    } catch (err) {
      URL.revokeObjectURL(previewUrl);
      toast.error('Failed to upload logo');
    } finally {
      setLogoLoading(false);
      if (logoInputRef.current) logoInputRef.current.value = '';
    }
  };

  const handleDeleteLogo = async () => {
    if (!editingReferral) return;
    setLogoLoading(true);
    try {
      await referralService.deleteLogo(editingReferral.id);
      setValue('logo', '');
      setEditingReferral({ ...editingReferral, logo: null });
      clearPendingLogo();
      table.loadData(() => referralService.getReferrals());
      toast.success('Logo removed successfully');
    } catch (err) {
      toast.error('Failed to remove logo');
    } finally {
      setLogoLoading(false);
    }
  };

  const onSubmit = (data: ReferralCreate) => {
    crud.handleCreateOrUpdate({
      ...data,
      ...(pendingLogoKey ? { logo: pendingLogoKey } : {}),
    }, editingReferral);
    clearPendingLogo();
  };

  const handleConfirmDelete = () => {
    crud.handleDelete(table.selectedItem);
  };

  const getContextMenuOptions = (): ContextMenuOption[] => {
    if (!table.selectedItem) return [];
    return [
      { label: 'Edit', icon: <EditIcon sx={{ mr: 2 }} />, onClick: handleEdit },
      { label: 'Delete', icon: <DeleteIcon sx={{ mr: 2 }} />, onClick: handleDelete, color: 'error' as const },
    ];
  };

  // Column value renderer
  const getColumnValue = (referral: Referral, columnKey: string): React.ReactNode => {
    switch (columnKey) {
      case 'logo':
        return referral.logo ? (
          <img
            src={`${referralService.getLogoUrl(referral.id)}?t=${new Date(referral.updated_at).getTime()}`}
            alt={`${referral.name} logo`}
            style={{ width: 60, height: 30, objectFit: 'contain' }}
          />
        ) : (
          <Typography variant="caption" color="text.secondary">—</Typography>
        );
      case 'name':
        return (
          <Typography
            variant="body2"
            onClick={(e) => {
              e.stopPropagation();
              table.handleMenuClose();
              openEditDialog(referral);
            }}
            sx={{
              cursor: 'pointer',
              textDecoration: 'underline',
              color: 'primary.main',
              '&:hover': { color: 'primary.dark' },
            }}
          >
            {referral.name}
          </Typography>
        );
      case 'phone': return referral.phone || 'N/A';
      case 'email': return referral.email || 'N/A';
      case 'hash':
        return (
          <Typography
            variant="body2"
            sx={{ fontFamily: 'monospace', cursor: 'pointer' }}
            title="Click to copy — add ?referral=<hash> to a public questionnaire link"
            onClick={(e) => {
              e.stopPropagation();
              navigator.clipboard?.writeText(referral.hash);
              toast.success('Referral hash copied');
            }}
          >
            {referral.hash}
          </Typography>
        );
      case 'createdAt': return formatDate(referral.created_at);
      default: return 'N/A';
    }
  };

  // Mobile card renderer
  const renderMobileCard = (referral: Referral) => (
    <>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2} sx={{ pr: 5 }}>
        <Box display="flex" alignItems="center" flex={1}>
          <ReferralIcon sx={{ mr: 1, color: 'text.secondary', fontSize: '1.5rem' }} />
          <Typography variant="h6" sx={{ fontWeight: 'medium' }}>
            {referral.name}
          </Typography>
        </Box>
      </Box>
      <Box sx={{ pl: 4 }}>
        {referral.phone && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>Phone: {referral.phone}</Typography>
        )}
        {referral.email && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>Email: {referral.email}</Typography>
        )}
      </Box>
    </>
  );

  const closeDialog = () => { setAddDialogOpen(false); clearPendingLogo(); };

  return (
    <RequirePermission check={canManageReferrals} featureName="referrals">
      <TablePageLayout loading={table.loading} error={table.error} errorPrefix="Error loading referrals:">
        <DataTableToolbar
          {...table.toolbarProps}
          title="Referrals"
          searchPlaceholder="Search referrals by name, phone, or email..."
          isAdmin={!!currentUser?.user_privilege?.config}
          actions={
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddReferral}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Add Referral
            </Button>
          }
        />

        {isMobile ? (
          <MobileCardGrid
            data={table.sortedData}
            renderCard={renderMobileCard}
            onMenuClick={table.handleRowClick}
            getItemKey={(r) => r.id}
            emptyIcon={<ReferralIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />}
            emptyMessage="No referrals found"
          />
        ) : (
          <DataTableContainer {...table.containerProps}>
            <DataTableBody
              data={table.sortedData}
              {...table.tableBodyProps}
              getColumnValue={getColumnValue}
              onRowClick={table.handleRowClick}
              getRowKey={(r) => r.id}
            />
          </DataTableContainer>
        )}

        <RowContextMenu {...table.menuProps} options={getContextMenuOptions()} />

        {/* Add/Edit Referral Dialog */}
        <ClinicDialog
          open={addDialogOpen}
          onClose={closeDialog}
          title={editingReferral ? 'Edit Referral' : 'Add New Referral'}
          onSubmit={handleSubmit(onSubmit)}
          actions={
            <>
              <Button type="submit" variant="contained" sx={CLINIC_DIALOG_SX.primaryButton}>
                {editingReferral ? 'Update' : 'Create'}
              </Button>
              <Button onClick={closeDialog} sx={CLINIC_DIALOG_SX.cancelButton}>
                Cancel
              </Button>
            </>
          }
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={CLINIC_DIALOG_SX.fieldRow}>
              <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>Name</Typography>
              <TextField
                {...register('name', { required: 'Referral name is required' })}
                fullWidth
                size="small"
                error={!!errors.name}
                helperText={errors.name?.message as string}
                InputProps={{ style: CLINIC_DIALOG_SX.textField as React.CSSProperties }}
              />
            </Box>

            {editingReferral && (
              <Box sx={CLINIC_DIALOG_SX.fieldRow}>
                <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>Referral Hash</Typography>
                <Box display="flex" gap={1} alignItems="center">
                  <TextField
                    value={editingReferral.hash}
                    fullWidth
                    size="small"
                    InputProps={{
                      readOnly: true,
                      style: CLINIC_DIALOG_SX.textField as React.CSSProperties,
                    }}
                    helperText="Append ?referral=<hash> to a public questionnaire link"
                  />
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      navigator.clipboard?.writeText(editingReferral.hash);
                      toast.success('Referral hash copied');
                    }}
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    Copy
                  </Button>
                </Box>
              </Box>
            )}

            <Box display="flex" gap={2}>
              <Box sx={{ ...CLINIC_DIALOG_SX.fieldRow, flex: 1 }}>
                <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>Phone</Typography>
                <TextField
                  {...register('phone')}
                  fullWidth
                  size="small"
                  InputProps={{ style: CLINIC_DIALOG_SX.textField as React.CSSProperties }}
                />
              </Box>
              <Box sx={{ ...CLINIC_DIALOG_SX.fieldRow, flex: 1 }}>
                <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>Email</Typography>
                <TextField
                  {...register('email')}
                  type="email"
                  fullWidth
                  size="small"
                  InputProps={{ style: CLINIC_DIALOG_SX.textField as React.CSSProperties }}
                />
              </Box>
            </Box>

            {/* Logo */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>Logo</Typography>
              <Box>
                {editingReferral ? (
                  <>
                    {(editingReferral.logo || pendingLogoPreview) && (
                      <Box mb={1}>
                        <img
                          src={pendingLogoPreview ?? `${referralService.getLogoUrl(editingReferral.id)}?t=${new Date(editingReferral.updated_at).getTime()}`}
                          alt="Referral logo"
                          style={{ width: 120, height: 60, objectFit: 'contain', border: '1px solid #ddd', borderRadius: 4 }}
                        />
                      </Box>
                    )}
                    <input
                      ref={logoInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      style={{ display: 'none' }}
                      onChange={handleLogoFileChange}
                    />
                    <Box display="flex" gap={1} flexWrap="wrap">
                      <Button variant="outlined" size="small" disabled={logoLoading} onClick={() => logoInputRef.current?.click()}>
                        {logoLoading ? 'Uploading...' : 'Upload Logo'}
                      </Button>
                      {editingReferral.logo && (
                        <Button variant="outlined" size="small" color="error" disabled={logoLoading} onClick={handleDeleteLogo}>
                          Remove Logo
                        </Button>
                      )}
                    </Box>
                  </>
                ) : (
                  <Typography variant="body2" color="text.secondary">Save the referral first to upload a logo</Typography>
                )}
              </Box>
            </Box>
          </Box>
        </ClinicDialog>

        {/* Delete Confirmation Dialog */}
        <ConfirmDeleteDialog
          open={deleteDialogOpen}
          onClose={() => { setDeleteDialogOpen(false); table.handleMenuClose(); }}
          onConfirm={handleConfirmDelete}
          entityType="Referral"
          entityName={table.selectedItem?.name}
        />

        <ManageColumnsModal {...table.manageColumnsProps} />
        <SaveViewModal {...table.saveViewModalProps} isAdmin={!!currentUser?.user_privilege?.config} />
      </TablePageLayout>
    </RequirePermission>
  );
};

export default ReferralsPage;
